import { initializeApp } from 'firebase/app';
import { getDatabase, ref, get, update, onValue, DataSnapshot, push } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import EdutechsCloudServer from './EdutechsCloudServer';
import { decodeString } from './client-utils';

export interface MeetingDatabaseObject {
    meetingId: string,
    createdByUID: string,
    createdAt: number,
    updatedAt: number,
    allowedParticipants: {
        [uid: string]: {
            name: string,
            email: string,
            uid: string,
            role: string,
            audio: boolean,
            video: boolean,
            screenShare: boolean,
            isMeetingBanned?: boolean,
            isChatBanned?: boolean,
        }
    },
    annonyMouseParticipantAllowed: boolean,
    password: string,
    meetingTitle: string,
    meetingDescription: string,
    meetingStatus: "active" | "ended",
    endedAt?: number,
    meetingCreatedUnder?: {
        grade: string,
        subject: string,
        batch: string,
        teacherUID: string
    }
}


export interface UserDetails {
    UID: string,
    UserEmail: string,
    UserInstitution: string,
    UserLocation: string,
    UserName: string,
    UserPhone: string,
    UserProffession: string,
    avatarURL: string,
    lastMeetingEndedAt?: number,
}


// Initialize Firebase
const config = {
    apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
    authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
    databaseURL: process.env.NEXT_PUBLIC_FIREBASE_DATABASE_URL,
    projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
    storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
    measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase only on client side
let auth: any;
let database: any;

if (typeof window !== 'undefined') {
    const app = initializeApp(config);
    auth = getAuth(app);
    database = getDatabase(app);
} else {
    // Create mock objects for server-side rendering
    auth = {};
    database = {};
}

export {
    database,
    auth
};

//this will check in our database if the meeting is valid and active to join
export async function validateMeetingId(meetingId: string): Promise<boolean> {
  if (typeof window === 'undefined') {
    return false; // Return false during server-side rendering
  }
  
  try {
    const meetingRef = ref(database, `openvidu-meetings/${meetingId}`);
    const snapshot = await get(meetingRef);

    console.log('Snapshot:', snapshot.val());
    //check if snapshot exists and meeting is active
    if (snapshot.exists() && snapshot.val().meetingStatus === "active") {
      return (true);
    } else {
      return (false);
    }
  } catch (error) {
    console.error('Error validating meeting ID:', error);
    return (false);
  }
}



//this will check to see if the user is allowed to join the meeting
export async function isUserAllowedToJoinMeetingFN(meetingId: string, userUID: string): Promise<boolean> {
    if (typeof window === 'undefined') {
        return false; // Return false during server-side rendering
    }
    
    const meetingRef = ref(database, `openvidu-meetings/${meetingId}/allowedParticipants/${userUID}`);
    const snapshot = await get(meetingRef);
    return (snapshot.exists());
}

export async function fetchUserDetails(userUID: string): Promise<UserDetails | null> {
    if (typeof window === 'undefined') {
        return null; // Return null during server-side rendering
    }
    
    const userRef = ref(database, `USERS/${userUID}/PublicInfo`);
    const snapshot = await get(userRef);
    return (snapshot.val());
}


export async function listenToLiveMeetingDetails(meetingId: string, callback: (snapshot: DataSnapshot) => void) {
    if (typeof window === 'undefined') {
        return; // Do nothing during server-side rendering
    }
    
    const meetingRef = ref(database, `openvidu-meetings/${meetingId}`);
    onValue(meetingRef, callback);
}

export async function closeAndEndMeeting(meetingId: string, adminUID: string, status: "active" | "ended") {
    if (typeof window === 'undefined') {
        return; // Do nothing during server-side rendering
    }

    if (status !== "active" && status !== "ended" || meetingId === '') {
        return;
    }

    const updates: { [key: string]: any } = {
       [`openvidu-meetings/${meetingId}/meetingStatus`]: status,
       [`openvidu-meetings/${meetingId}/endedAt`]: new Date().getTime(),
       [`openvidu-meetings-history/${adminUID}/${meetingId}/meetingStatus`]: status,
       [`openvidu-meetings-history/${adminUID}/${meetingId}/endedAt`]: new Date().getTime(),
       [`USERS/${adminUID}/PublicInfo/lastMeetingEndedAt`]: new Date().getTime()
    };

    console.log('Updates:', updates);

    // Update all locations atomically using Firebase v9+ syntax
    const rootRef = ref(database);
    await update(rootRef, updates);
}

export async function flipStatusOfParticipant(meetingId: string, userUID: string, status: boolean, keyName: "audio" | "video" | "screenShare" | "isMeetingBanned" | "isChatBanned") {
    if (typeof window === 'undefined') {
        return; // Do nothing during server-side rendering
    }
    
    const meetingRef = ref(database, `openvidu-meetings/${meetingId}/allowedParticipants/${userUID}/`);
    await update(meetingRef, { [keyName]: !status });
    return (true);
}


export async function getCustomAuthToken_SSO_KEY({uid, createdByUid, sessionPurpose}: {uid: string, createdByUid: string | undefined, sessionPurpose: string}): Promise<string>{
    let response = await EdutechsCloudServer.postRequestPromise('api-get-custom-token-for-auth-edutechs', {
        uid, createdByUid, 
        sessionPurpose, 
    }, false)

    if (!response.customToken){
        console.error('Invalid Custom Auth Token');
        return 'INVALID-TOKEN-SSO-KEY';
    }

    //store the custom auth token in the database
    const ssoRef = ref(database, 'login-auth-keys');
    const newSsoRef = push(ssoRef);
    const ssoPushKey = newSsoRef.key as string;
    
    await update(newSsoRef, {
        createdAt: new Date().getTime(),
        updatedAt: new Date().getTime(),
        isValid: true,
        forSessionPurpose: sessionPurpose,
        forUserUID: uid,
        token: response.customToken,
    }); 

    return ssoPushKey;
}


export async function takeAttendanceOfParticipants(meetingId: string, adminUID: string, activeParticipantsUIDs: string[]): Promise<void> {
    if (typeof window === 'undefined') {
        return; // Do nothing during server-side rendering
    }
    
    //need to fetch the grade, subject and batchUID
    const meetingRef = ref(database, `openvidu-meetings/${meetingId}`);
    const snapshot = await get(meetingRef);
    const meetingDetails = snapshot.val();
    if (!meetingDetails) {
        return;
    }
    const grade             = meetingDetails.meetingCreatedUnder?.grade;
    const subject           = meetingDetails.meetingCreatedUnder?.subject;
    const batchUID          = meetingDetails.meetingCreatedUnder?.batch;
    const teacherUID        = meetingDetails.meetingCreatedUnder?.teacherUID;

    const now               = new Date();
    const day               = String(now.getDate()).padStart(2, '0');
    const month             = String(now.getMonth() + 1).padStart(2, '0');
    const year              = now.getFullYear();
    const datepickerValue   = `${day}-${month}-${year}`;


    let updates: { [key: string]: any } = {};


    activeParticipantsUIDs.forEach((studentUID) => {
        let dbAddress = `USERS/${teacherUID}/UserClass/${grade}/${subject}/Streams/${batchUID}/AcceptedStudents/${studentUID}/Attendance/${datepickerValue}`;
        updates[dbAddress] = {
            status          : 'Present',
            modifiedTime    : new Date().getTime(),
            modifiedBy      : adminUID,
            fromVideoConferenceMeetingId: meetingId
        };
    });


    //update possibledates in db
    const possibleDatesRef  = ref(database, `USERS/${teacherUID}/UserClass/${grade}/${subject}/Streams/${batchUID}/PossibleDates`);
    const snapshot2         = await get(possibleDatesRef);
    const dates             = snapshot2.val() ? snapshot2.val() : {};
    let newDates            = [];

    for (let x in dates) {
        newDates.push(dates[x]);
    }

    let found = newDates.find((date) => date.value === datepickerValue);
    if (!found) {
        let dbAddress = `USERS/${teacherUID}/UserClass/${grade}/${subject}/Streams/${batchUID}/PossibleDates`;
        let dbData = {
            value: datepickerValue,
        };
        const possibleDatesRef = ref(database, dbAddress);
        const newCreatedPushKey = push(possibleDatesRef);
        const createdPushKeyString = newCreatedPushKey.key as string;
        updates[`${dbAddress}/${createdPushKeyString}`] = dbData;
    }

    const rootRef = ref(database);
    return await update(rootRef, updates);
}


export async function uploadVideoDetailsToDatabase(filepath: string, roomTitle: string, teacherUID: string, grade: string, subject: string, batch: string) {
    if (typeof window === 'undefined') {
        return; // Do nothing during server-side rendering
    }

    let uiBatchNameSnapshot     = await get(ref(database, `USERS/${teacherUID}/UserClass/${grade}/${subject}/Streams/${batch}/BatchName`));
    let uiBatchName             = uiBatchNameSnapshot.val() ? uiBatchNameSnapshot.val() : 'Unknown Class';

    const videoFileData = {
        title       : `${decodeString(roomTitle)}`,
        visibility  : 'Private',
        description : `Uploaded from EduMeet Live Class on ${new Date().toLocaleString()} for ${grade} - ${uiBatchName}`,
        thumbnail   : `https://edutechs.app/assets/images/videoDefaultThumbnail.jpg`, // default thumbnail for bulk upload videos
        fileURL     : `https://openvidurecordings-02.s3.ap-south-1.amazonaws.com/${filepath}`,
        fileSize    : 0, // can be updated later if needed
        fileId      : filepath, // use the object key itself
        fileName    : filepath, // same as fileId
        bucketId    : "openvidurecordings-02",
        contentType : 'video/mp4',
        uploadTime  : Date.now(),
    }

    let pushKey = new Date().getTime();

    const videoFileDataRef = ref(database, `USERS/${teacherUID}/VideoCourse/AllVideos/AllIncludedVideos/${pushKey}`);
    await update(videoFileDataRef, videoFileData);

    //need to see if need to add this video under a specific video group
    const defaultRecordingVideoGroupRef     = ref(database, `USERS/${teacherUID}/UserClass/${grade}/${subject}/Streams/${batch}/OpenViduRecordingAutoUploadGroupKey`);
    const snapshot                          = await get(defaultRecordingVideoGroupRef);
    const defaultRecordingVideoGroup        = snapshot.val() ? snapshot.val() : 'default';

    console.log('defaultRecordingVideoGroup: ', defaultRecordingVideoGroup);
    
    if (defaultRecordingVideoGroup !== 'default') {
        let groupKey                        = defaultRecordingVideoGroup;
        let currentVideoAddedToGroupRef     = ref(database, `USERS/${teacherUID}/VideoCourse/GroupVideos/${groupKey}/IncludeVideos`);
        let currentVideoAddedToGroupSnap    = await get(currentVideoAddedToGroupRef);
        let currentVideoAddedToGroupString  = currentVideoAddedToGroupSnap.exists() ? currentVideoAddedToGroupSnap.val() : '';
    
        console.log('currentVideoAddedToGroupString: ', currentVideoAddedToGroupString);
        
        const groupVideosRef = ref(database, `USERS/${teacherUID}/VideoCourse/GroupVideos/${groupKey}/`);
        await update(groupVideosRef, {
            'IncludeVideos': currentVideoAddedToGroupString === '' ? String(pushKey) : `${currentVideoAddedToGroupString},${pushKey}`
        });
    }
}